import React, { useState, useEffect, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin, Button, message, Input, List, Avatar, Segmented } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInbox,
  faPaperPlane,
  faArrowLeft,
  faPhone,
  faComment,
  faEnvelope,
  faFileLines,
  faGear,
  faUser,
  faMagnifyingGlass,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { usePageHeader } from '../../layouts/AdminLayout';
import PatientService from '../../services/PatientService';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useActiveCall } from '../../contexts/ActiveCallContext';
import { useStore } from '../../hooks/storeContext';
import api from '../../services/api';
import { INBOX_ENDPOINTS } from '../../constants/api';
import {
  PageContainer,
  InboxContainer,
  ThreadListPanel,
  ThreadListHeader,
  SearchInput,
  ThreadList,
  ThreadItem,
  ThreadAvatar,
  ThreadInfo,
  ThreadName,
  ThreadPreview,
  UnreadBadge,
  ConversationPanel,
  ConversationHeader,
  ConversationInfo,
  ConversationActions,
  MessageList,
  MessageBubble,
  MessageInputArea,
  MessageTextarea,
  SendButton,
  EmptyState,
  LoadingSpinner,
  NewConversationButton,
  StyledTabs,
  TabsBorderDiv,
  TemplateList,
  TemplateCard,
  SettingsPanel
} from './styled';

// Default Black Heart Association SMS number (Twilio TWILIO_PHONE_NUMBER, +14697578744).
const DEFAULT_FROM_NUMBER = '(469) 757-8744';

// Default Black Heart Association email identity for the shared inbox.
const DEFAULT_FROM_EMAIL = 'info@blackheartassociation.org';

// Per-user inbox preference storage keys.
const AUTO_ASSIGN_STORAGE_KEY = 'inboxAutoAssign';
const NOTIFICATION_SOUND_STORAGE_KEY = 'inboxNotificationSound';

// Friendly labels for Twilio message delivery statuses.
const STATUS_LABELS = {
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
  received: 'Received',
  read: 'Read'
};

// Compare phone numbers by their last 10 digits (ignores formatting).
const phoneDigits = (value) => (value || '').replace(/\D/g, '').slice(-10);

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d`;
  
  return date.toLocaleDateString();
};

// Format message timestamp
const formatMessageTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  return date.toLocaleDateString('en-US', options);
};

const Inbox = observer(() => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [channel, setChannel] = useState('sms');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showMobileConversation, setShowMobileConversation] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [composingNew, setComposingNew] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [autoAssign, setAutoAssign] = useState(
    () => localStorage.getItem(AUTO_ASSIGN_STORAGE_KEY) === 'true'
  );
  const [notificationSound, setNotificationSound] = useState(() => {
    const stored = localStorage.getItem(NOTIFICATION_SOUND_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  const messageListRef = useRef(null);
  const textareaRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const wsRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const loadConversationsRef = useRef(null);
  const { setPageHeader } = usePageHeader();
  const navigate = useNavigate();
  const { initiateCall } = useActiveCall();
  const { authStore } = useStore();

  // Calculate total unread count for header
  const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);

  // Set page header with dynamic count
  useEffect(() => {
    setPageHeader({
      title: 'Inbox',
      count: totalUnread,
      countIcon: <FontAwesomeIcon icon={faInbox} />,
      loading: loading,
      loadingType: 'heart'
    });
  }, [setPageHeader, totalUnread, loading]);

  // Load conversations on mount and whenever the SMS/Email channel changes.
  useEffect(() => {
    loadConversations();
    setSelectedConversation(null);
    setMessages([]);
    setShowMobileConversation(false);
    setComposingNew(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  // Load real members when the New Message composer is open (search-as-you-type,
  // debounced). Uses the same members-queue endpoint as the All Members page so
  // results are consistent (backend search across the full dataset, not capped).
  useEffect(() => {
    if (!composingNew) return undefined;
    let cancelled = false;
    setMembersLoading(true);
    const trimmedQuery = memberSearchQuery.trim();
    const timer = setTimeout(async () => {
      try {
        const filterParams = {
          tabFilter: [
            'completed',
            'in_progress',
            'pending_review',
            'new_patient',
            'matched',
            'needs_attention',
            'duplicate'
          ],
          page: 1,
          limit: 25,
          sort: 'newest'
        };
        if (trimmedQuery) {
          filterParams.search = trimmedQuery;
        }
        const response = await PatientService.getAllEventsPatientQueue(filterParams);
        const records = response?.data || [];
        const seen = new Set();
        const normalized = [];
        records.forEach((record) => {
          const p = record.patient || record;
          const id = p.patientId || record.patientId;
          if (!id || seen.has(id)) return;
          seen.add(id);
          normalized.push({
            id,
            name: `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Unknown',
            phoneNumber: p.phoneNumber || '',
            email: p.emailAddress || ''
          });
        });
        if (cancelled) return;
        setMembers(normalized);
      } catch (error) {
        if (!cancelled) setMembers([]);
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [composingNew, memberSearchQuery]);

  // Play a short notification beep using the Web Audio API (no asset needed).
  const playNotificationSound = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;
      const ctx = new AudioContextClass();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.3);
      oscillator.onended = () => ctx.close();
    } catch (e) {
      // Audio playback can fail silently (e.g., autoplay restrictions); ignore.
    }
  }, []);

  // Persist + apply the Auto-assign preference.
  const handleToggleAutoAssign = (e) => {
    const checked = e.target.checked;
    setAutoAssign(checked);
    localStorage.setItem(AUTO_ASSIGN_STORAGE_KEY, String(checked));
  };

  // Persist the Notification sound preference; play a preview when enabling.
  const handleToggleNotificationSound = (e) => {
    const checked = e.target.checked;
    setNotificationSound(checked);
    localStorage.setItem(NOTIFICATION_SOUND_STORAGE_KEY, String(checked));
    if (checked) playNotificationSound();
  };

  // Play the notification sound when a new inbound message arrives.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (
      messages.length > prevMessageCountRef.current &&
      last &&
      !last.outbound &&
      notificationSound
    ) {
      playNotificationSound();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, notificationSound, playNotificationSound]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // Keep refs in sync so the WebSocket handler can read the latest values.
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Connect to the inbox WebSocket for live inbound messages + status updates.
  useEffect(() => {
    let ws;
    let reconnectTimer;
    let closedByUs = false;

    const connect = () => {
      try {
        ws = new WebSocket(INBOX_ENDPOINTS.WEBSOCKET);
      } catch (e) {
        return;
      }
      wsRef.current = ws;

      ws.onmessage = (event) => {
        let payload;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        const { type, data } = payload;
        if (!type || !data) return;

        if (type === 'inbox:newMessage') {
          const sel = selectedConversationRef.current;
          if (sel && String(sel.id) === String(data.conversationId)) {
            setMessages((prev) =>
              prev.some((m) => String(m.id) === String(data.message.id))
                ? prev
                : [...prev, data.message]
            );
          }
          // Refresh the thread list so the conversation + preview update.
          loadConversationsRef.current?.();
        } else if (type === 'inbox:conversationUpdated') {
          loadConversationsRef.current?.();
        } else if (type === 'inbox:messageStatusUpdated') {
          const sel = selectedConversationRef.current;
          if (sel && String(sel.id) === String(data.conversationId)) {
            setMessages((prev) =>
              prev.map((m) =>
                String(m.id) === String(data.messageId) ? { ...m, status: data.status } : m
              )
            );
          }
        }
      };

      ws.onclose = () => {
        if (!closedByUs) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
    };

    connect();

    return () => {
      closedByUs = true;
      clearTimeout(reconnectTimer);
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, []);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/inbox/conversations', { channel });
      setConversations(res?.data?.conversations || []);
    } catch (error) {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };
  loadConversationsRef.current = loadConversations;

  const loadMessages = async (conversationId) => {
    // Local draft conversations have no server messages yet.
    if (!conversationId || String(conversationId).startsWith('new-')) {
      setMessages([]);
      return;
    }
    setMessagesLoading(true);
    try {
      const res = await api.get(`/inbox/conversations/${conversationId}/messages`);
      setMessages(res?.data || []);
    } catch (error) {
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setShowMobileConversation(true);
    setActiveTab('messages');
    setNewSubject('');
    loadMessages(conversation.id);
    
    // Mark as read in local state
    setConversations(prev => 
      prev.map(c => 
        c.id === conversation.id ? { ...c, unreadCount: 0 } : c
      )
    );
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const text = newMessage.trim();
    const isDraft = String(selectedConversation.id).startsWith('new-');
    const convChannel = selectedConversation.channel || 'sms';
    const isEmail = convChannel === 'email';

    if (isDraft && isEmail && !selectedConversation.email) {
      message.error('This member has no email address on file');
      return;
    }

    const subject = (newSubject || selectedConversation.subject || '').trim();
    setSendingMessage(true);

    try {
      const endpoint = isDraft
        ? '/inbox/conversations/new/messages'
        : `/inbox/conversations/${selectedConversation.id}/messages`;

      let payload;
      if (isDraft && isEmail) {
        payload = {
          text,
          channel: 'email',
          email: selectedConversation.email,
          contactName: selectedConversation.contactName,
          patientId: selectedConversation.patientId,
          subject: subject || undefined
        };
      } else if (isDraft) {
        payload = {
          text,
          channel: 'sms',
          phoneNumber: selectedConversation.phoneNumber,
          contactName: selectedConversation.contactName
        };
      } else {
        payload = { text, channel: convChannel };
        if (isEmail && subject) payload.subject = subject;
      }

      const res = await api.post(endpoint, payload);
      if (!res?.success) {
        throw new Error(res?.message || res?.error || 'Send failed');
      }

      setNewMessage('');
      setNewSubject('');

      if (isDraft) {
        // The send created the conversation server-side; reload and reselect it
        // so subsequent sends and delivery-status updates use the real id.
        const convRes = await api.get('/inbox/conversations', { channel: convChannel });
        const list = convRes?.data?.conversations || [];
        setConversations(list);
        const match = isEmail
          ? list.find(
            (c) => (c.email || '').toLowerCase() === (selectedConversation.email || '').toLowerCase()
          )
          : list.find(
            (c) => phoneDigits(c.phoneNumber) === phoneDigits(selectedConversation.phoneNumber)
          );
        if (match) {
          setSelectedConversation({ ...match, patientId: selectedConversation.patientId });
          await loadMessages(match.id);
        } else {
          setMessages((prev) => [...prev, res.data]);
        }
      } else {
        setMessages((prev) => [...prev, res.data]);
        setConversations((prev) =>
          prev.map((c) =>
            c.id === selectedConversation.id
              ? { ...c, lastMessage: text, lastMessageTime: new Date().toISOString() }
              : c
          )
        );
      }

      if (res.data?.status === 'failed') {
        message.error(isEmail ? 'Email failed to send' : 'Message failed to send');
      } else {
        message.success(isEmail ? 'Email sent' : 'Message sent');
      }
    } catch (error) {
      message.error(error.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    // For email, Enter inserts a newline; sending is via the Send button only.
    if ((selectedConversation?.channel || 'sms') === 'email') return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleBackToList = () => {
    setShowMobileConversation(false);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return '#7c9eb2';
    // Muted, softer color palette with variety
    const colors = ['#7c9eb2', '#8bb894', '#c9a86c', '#c98b8b', '#a893c2', '#7db5b5', '#c28da8'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Selecting a member opens the main conversation UI for that member.
  const handleSelectMember = (member) => {
    if (!member) return;

    if (channel === 'email') {
      if (!member.email) {
        message.info('This member has no email address on file');
        return;
      }
      // Reuse an existing email thread for this address if present.
      const existing = conversations.find(
        (c) => (c.email || '').toLowerCase() === (member.email || '').toLowerCase()
      );
      const conversation = existing || {
        id: `new-${Date.now()}`,
        channel: 'email',
        contactName: member.name,
        email: member.email,
        patientId: member.id,
        assignedTo: autoAssign ? (authStore?.me?.id || null) : null,
        assignedName: autoAssign ? (authStore?.me?.fullName || 'you') : null,
        subject: '',
        lastMessage: '',
        lastMessageTime: new Date().toISOString(),
        unreadCount: 0,
        tags: []
      };

      setSelectedConversation({ ...conversation, patientId: member.id });
      setNewSubject(existing?.subject || '');
      if (existing) {
        loadMessages(existing.id);
      } else {
        setMessages([]);
      }
      setComposingNew(false);
      setMemberSearchQuery('');
      setShowMobileConversation(true);
      setActiveTab('messages');
      return;
    }

    // Reuse an existing conversation for this phone number if present.
    const existing = conversations.find(
      (c) => phoneDigits(c.phoneNumber) === phoneDigits(member.phoneNumber)
    );
    const conversation = existing || {
      id: `new-${Date.now()}`,
      channel: 'sms',
      contactName: member.name,
      phoneNumber: member.phoneNumber,
      patientId: member.id,
      assignedTo: autoAssign ? (authStore?.me?.id || null) : null,
      assignedName: autoAssign ? (authStore?.me?.fullName || 'you') : null,
      lastMessage: '',
      lastMessageTime: new Date().toISOString(),
      unreadCount: 0,
      tags: []
    };

    setSelectedConversation({ ...conversation, patientId: member.id });
    if (existing) {
      loadMessages(existing.id);
    } else {
      setMessages([]);
    }

    // Leave the new-message composer and show the conversation UI.
    setComposingNew(false);
    setMemberSearchQuery('');
    setShowMobileConversation(true);
    setActiveTab('messages');
  };

  // Open the inline new-message composer
  const handleStartNewMessage = () => {
    setComposingNew(true);
    setSelectedConversation(null);
    setShowMobileConversation(true);
    setMemberSearchQuery('');
  };

  // Cancel the inline new message composer
  const handleCancelNewMessage = () => {
    setComposingNew(false);
    setMemberSearchQuery('');
  };

  // Filter conversations based on search
  const filteredConversations = conversations.filter(c =>
    c.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phoneNumber?.includes(searchQuery) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Predefined message templates
  const messageTemplates = [
    {
      id: 1,
      name: 'Appointment Reminder',
      category: 'Reminders',
      text: 'Hi {name}! This is a reminder about your upcoming health screening appointment. Please remember to bring your ID and insurance card. Reply YES to confirm or call us to reschedule.'
    },
    {
      id: 2,
      name: 'Follow-up Results',
      category: 'Results',
      text: 'Hi {name}, your screening results are ready for review. Please call our office or schedule a follow-up appointment to discuss your results with our team.'
    },
    {
      id: 3,
      name: 'Reschedule Request',
      category: 'Scheduling',
      text: 'Hi {name}, we noticed you missed your recent appointment. Would you like to reschedule? Reply with your preferred date and time, and we\'ll confirm availability.'
    },
    {
      id: 4,
      name: 'Thank You',
      category: 'General',
      text: 'Thank you for visiting Black Heart Association! We appreciate you taking the time to prioritize your health. If you have any questions about your visit, please don\'t hesitate to reach out.'
    },
    {
      id: 5,
      name: 'Documents Needed',
      category: 'Documents',
      text: 'Hi {name}, we need some additional documentation to complete your file. Please bring the following items to your next visit or upload them through our patient portal.'
    },
    {
      id: 6,
      name: 'Event Invitation',
      category: 'Events',
      text: 'Hi {name}! Black Heart Association is hosting a free health screening event in your area. Would you like to reserve a spot? Reply YES for more details.'
    }
  ];

  const handleUseTemplate = (template) => {
    const personalizedText = selectedConversation?.contactName
      ? template.text.replace(/{name}/g, selectedConversation.contactName.split(' ')[0])
      : template.text.replace(/{name}/g, 'there');
    setNewMessage(personalizedText);
    setActiveTab('messages');
  };

  // Open the selected contact's member record
  const handleViewProfile = () => {
    if (!selectedConversation?.patientId) {
      message.info('No member record linked to this conversation');
      return;
    }
    navigate(ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', selectedConversation.patientId));
  };

  // Start a call to the selected contact via the global dialer
  const handleCallContact = () => {
    if (!selectedConversation?.phoneNumber) {
      message.info('No phone number on file for this contact');
      return;
    }
    initiateCall({
      fullNumber: selectedConversation.phoneNumber,
      contact: {
        name: selectedConversation.contactName,
        phoneNumber: selectedConversation.phoneNumber
      },
      fromNumber: DEFAULT_FROM_NUMBER,
      callType: 'outbound',
      patientId: selectedConversation.patientId
    });
  };

  // Tab items for Ant Design Tabs
  const tabItems = [
    {
      key: 'messages',
      label: (
        <span>
          <FontAwesomeIcon icon={faComment} /> Messages
        </span>
      )
    },
    {
      key: 'templates',
      label: (
        <span>
          <FontAwesomeIcon icon={faFileLines} /> Templates
        </span>
      )
    },
    {
      key: 'settings',
      label: (
        <span>
          <FontAwesomeIcon icon={faGear} /> Settings
        </span>
      )
    }
  ];

  return (
    <PageContainer>
      {/* Tab Bar */}
      <StyledTabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={tabItems}
      />
      <TabsBorderDiv />

      <InboxContainer>
      {/* Thread List Panel */}
      <ThreadListPanel showConversation={showMobileConversation}>
        <ThreadListHeader>
          <Segmented
            block
            value={channel}
            onChange={(val) => setChannel(val)}
            options={[
              { label: <span><FontAwesomeIcon icon={faComment} /> SMS</span>, value: 'sms' },
              { label: <span><FontAwesomeIcon icon={faEnvelope} /> Email</span>, value: 'email' }
            ]}
            style={{ marginBottom: '12px' }}
          />
          <NewConversationButton onClick={handleStartNewMessage}>
            <FontAwesomeIcon icon={faPlus} /> {channel === 'email' ? 'New Email' : 'New Message'}
          </NewConversationButton>
          <SearchInput
            placeholder={channel === 'email' ? 'Search emails...' : 'Search conversations...'}
            value={searchQuery}
            onChange={handleSearch}
            style={{ marginTop: '12px' }}
          />
        </ThreadListHeader>
        
        <ThreadList>
          {loading ? (
            <LoadingSpinner>
              <Spin />
            </LoadingSpinner>
          ) : filteredConversations.length === 0 ? (
            <EmptyState>
              <div className="subtitle">No conversations found</div>
            </EmptyState>
          ) : (
            filteredConversations.map(conversation => (
              <ThreadItem
                key={conversation.id}
                active={selectedConversation?.id === conversation.id}
                onClick={() => handleSelectConversation(conversation)}
              >
                <ThreadAvatar color={getAvatarColor(conversation.contactName)}>
                  {getInitials(conversation.contactName)}
                </ThreadAvatar>
                <ThreadInfo>
                  <ThreadName>
                    <span className="name">{conversation.contactName || 'Unknown'}</span>
                    <span className="time">{formatRelativeTime(conversation.lastMessageTime)}</span>
                  </ThreadName>
                  <ThreadPreview>
                    <span className="preview-text">{conversation.lastMessage || 'No messages yet'}</span>
                    {conversation.unreadCount > 0 && (
                      <UnreadBadge>{conversation.unreadCount}</UnreadBadge>
                    )}
                  </ThreadPreview>
                </ThreadInfo>
              </ThreadItem>
            ))
          )}
        </ThreadList>
      </ThreadListPanel>

      {/* Conversation Panel */}
      <ConversationPanel showConversation={showMobileConversation}>
        {composingNew ? (
          <>
            <ConversationHeader>
              <ConversationInfo>
                <button className="back-button" onClick={handleCancelNewMessage}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <div>
                  <div className="contact-name">{channel === 'email' ? 'New Email' : 'New Message'}</div>
                  <div className="contact-phone" style={{ fontSize: '12px', color: '#999' }}>
                    From: {channel === 'email' ? DEFAULT_FROM_EMAIL : DEFAULT_FROM_NUMBER}
                  </div>
                </div>
              </ConversationInfo>
            </ConversationHeader>
            <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
              <Input
                placeholder="Search members by name, phone, or email..."
                prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#bbb' }} />}
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
                style={{ marginBottom: '16px' }}
                allowClear
              />

              <div style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
                <List
                  dataSource={members}
                  loading={membersLoading}
                  locale={{ emptyText: membersLoading ? 'Loading members...' : 'No members found' }}
                  renderItem={(member) => (
                    <List.Item
                      key={member.id}
                      onClick={() => handleSelectMember(member)}
                      style={{ padding: '12px 16px', cursor: 'pointer', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar style={{ backgroundColor: getAvatarColor(member.name) }}>
                            {getInitials(member.name)}
                          </Avatar>
                        }
                        title={<span style={{ fontWeight: 600 }}>{member.name}</span>}
                        description={
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            <div>{member.phoneNumber}</div>
                            <div>{member.email}</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </div>
          </>
        ) : selectedConversation ? (
          <>
            <ConversationHeader>
              <ConversationInfo>
                <button className="back-button" onClick={handleBackToList}>
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <div>
                  <div className="contact-name">{selectedConversation.contactName || 'Unknown'}</div>
                  {(selectedConversation.channel || 'sms') === 'email' ? (
                    <>
                      <div className="contact-phone">
                        To: <span style={{ color: '#1890ff' }}>{selectedConversation.email}</span>
                      </div>
                      <div className="contact-phone" style={{ fontSize: '12px', color: '#999' }}>
                        From: {DEFAULT_FROM_EMAIL}
                      </div>
                      {(newSubject || selectedConversation.subject) && (
                        <div className="contact-phone" style={{ fontSize: '12px', color: '#555', fontWeight: 600 }}>
                          Subject: {newSubject || selectedConversation.subject}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="contact-phone">
                        To: <span style={{ color: '#1890ff' }}>{selectedConversation.phoneNumber}</span>
                      </div>
                      <div className="contact-phone" style={{ fontSize: '12px', color: '#999' }}>
                        From: {DEFAULT_FROM_NUMBER}
                      </div>
                    </>
                  )}
                  {selectedConversation.assignedTo && (
                    <div className="contact-phone" style={{ fontSize: '12px', color: '#52c41a' }}>
                      Assigned to {selectedConversation.assignedName || 'you'}
                    </div>
                  )}
                </div>
              </ConversationInfo>
              <ConversationActions>
                {(selectedConversation.channel || 'sms') !== 'email' && (
                  <Button icon={<FontAwesomeIcon icon={faPhone} />} style={{ marginRight: '8px' }} onClick={handleCallContact}>Call</Button>
                )}
                <Button icon={<FontAwesomeIcon icon={faUser} />} onClick={handleViewProfile}>Profile</Button>
              </ConversationActions>
            </ConversationHeader>
            
            {/* Messages Tab Content */}
            {activeTab === 'messages' && (
              <>
                <MessageList ref={messageListRef}>
                  {messagesLoading ? (
                    <LoadingSpinner>
                      <Spin />
                    </LoadingSpinner>
                  ) : messages.length === 0 ? (
                    <EmptyState>
                      <div className="subtitle">No messages yet. Send one to start the conversation!</div>
                    </EmptyState>
                  ) : (
                    messages.map(msg => (
                      <MessageBubble key={msg.id} outbound={msg.outbound}>
                        {msg.subject && (msg.channel === 'email') && (
                          <div style={{ fontWeight: 600, marginBottom: '4px', opacity: 0.85 }}>
                            {msg.subject}
                          </div>
                        )}
                        {msg.text}
                        <div className="message-time">
                          {formatMessageTime(msg.timestamp)}
                          {msg.sentBy && <span> · {msg.sentBy}</span>}
                          {msg.outbound && msg.status && (
                            <span style={{ color: msg.status === 'failed' ? '#ff4d4f' : undefined }}>
                              {' '}· {STATUS_LABELS[msg.status] || msg.status}
                            </span>
                          )}
                        </div>
                      </MessageBubble>
                    ))
                  )}
                </MessageList>
                
                <MessageInputArea style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                  {(selectedConversation.channel || 'sms') === 'email' &&
                    String(selectedConversation.id).startsWith('new-') && (
                    <Input
                      placeholder="Subject"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      style={{ marginBottom: '8px', width: '100%' }}
                    />
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-end', width: '100%' }}>
                    <MessageTextarea
                      ref={textareaRef}
                      placeholder={(selectedConversation.channel || 'sms') === 'email'
                        ? 'Type your email...'
                        : 'Type your message...'}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      rows={1}
                    />
                    <SendButton
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                    >
                      {sendingMessage ? <Spin size="small" /> : <FontAwesomeIcon icon={faPaperPlane} />}
                    </SendButton>
                    <div style={{ fontSize: '11px', color: '#999', marginLeft: '8px' }}>
                      {(selectedConversation.channel || 'sms') === 'email'
                        ? `${newMessage.length}`
                        : `${newMessage.length} / 1600`}
                    </div>
                  </div>
                </MessageInputArea>
              </>
            )}
            
            {/* Templates Tab Content */}
            {activeTab === 'templates' && (
              <TemplateList>
                <div style={{ marginBottom: '16px', color: '#666', fontSize: '13px' }}>
                  Click a template to use it. Variables like {'{name}'} will be replaced automatically.
                </div>
                {messageTemplates.map(template => (
                  <TemplateCard key={template.id} onClick={() => handleUseTemplate(template)}>
                    <div className="template-name">
                      <span>{template.name}</span>
                      <span className="template-category">{template.category}</span>
                    </div>
                    <div className="template-preview">{template.text}</div>
                  </TemplateCard>
                ))}
              </TemplateList>
            )}
            
            {/* Settings Tab Content */}
            {activeTab === 'settings' && (
              <SettingsPanel>
                <div className="settings-section">
                  <h3>Conversation Settings</h3>
                  <div className="settings-item">
                    <div className="settings-label">
                      Auto-assign to me
                      <div className="settings-description">Automatically assign new replies to you</div>
                    </div>
                    <input type="checkbox" checked={autoAssign} onChange={handleToggleAutoAssign} />
                  </div>
                  <div className="settings-item">
                    <div className="settings-label">
                      Notification sound
                      <div className="settings-description">Play a sound for new messages</div>
                    </div>
                    <input type="checkbox" checked={notificationSound} onChange={handleToggleNotificationSound} />
                  </div>
                </div>
                <div className="settings-section">
                  <h3>Messaging Number</h3>
                  <div className="settings-item">
                    <div className="settings-label">
                      Send From
                      <div className="settings-description">Default Black Heart Association number.</div>
                    </div>
                    <span style={{ color: '#999' }}>{DEFAULT_FROM_NUMBER}</span>
                  </div>
                </div>
                <div className="settings-section">
                  <h3>Contact Info</h3>
                  <div className="settings-item">
                    <div className="settings-label">Phone Number</div>
                    <span style={{ color: '#666' }}>{selectedConversation.phoneNumber || '—'}</span>
                  </div>
                </div>
              </SettingsPanel>
            )}
          </>
        ) : (
          <EmptyState>
            <FontAwesomeIcon icon={channel === 'email' ? faEnvelope : faComment} style={{ fontSize: '64px', marginBottom: '16px', opacity: 0.5 }} />
            <div className="title">Select a conversation</div>
            <div className="subtitle">Choose a conversation from the list to view messages</div>
          </EmptyState>
        )}
      </ConversationPanel>
    </InboxContainer>
    </PageContainer>
  );
});

export default Inbox;
